import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Txt } from '@vst/beam';
import { useTranslation } from 'react-i18next';

import { AddonIcon } from '@app/components/AddonIcon';
import {
  getBillingAccount,
  getCharacteristics,
  getCurrentAddOns,
  getEtf,
  selectAddOns,
  selectBillingAccount,
  selectEtf,
  selectIsPreinstall,
  selectPlanCharacteristics,
} from '@app/redux';
import { getNextBillCycleDate } from '@app/utils';

import EarlyTerminationFee from '../EarlyTerminationFee';
import LoadingCard from '../LoadingCard';
import MultilineLoading from '../MultilineLoading';
import SubscriptionCard from './SubscriptionCard';
import { ProductInstanceTypes } from '../../../../../../platform/src/server/dataSources/PSM/types';

import styles from './index.module.scss';

const DisconnectCart = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation('Disconnect');

  const billingAccount = useSelector(selectBillingAccount);
  const { currentAddOns, loading: addonsLoading } = useSelector(selectAddOns);
  const { characteristics, loading: planLoading } = useSelector(selectPlanCharacteristics);
  const { data } = useSelector(selectEtf);

  // Boolean is used to ensure etf is a true/false value based on whether `data.calculatedEtf.value` exists and is non-zero.
  const etf = Boolean(data?.calculatedEtf?.value);
  const hasPreinstall = useSelector(selectIsPreinstall);

  useEffect(() => {
    dispatch(getEtf());

    if (!characteristics.name) dispatch(getCharacteristics());
    if (!currentAddOns) dispatch(getCurrentAddOns());
    if (!billingAccount.nextBillPeriodStartDate) dispatch(getBillingAccount());
  }, [billingAccount.nextBillPeriodStartDate, characteristics.name, currentAddOns, dispatch]);

  const nextBillingCycleDate = getNextBillCycleDate(billingAccount);

  return (
    <div>
      <Txt variant="heading6" mb="16px">
        {t('cart.subscriptions.heading')}
      </Txt>
      <Txt variant="bodySmallRegular" mb="24px">
        {t('cart.subscriptions.description')}
      </Txt>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        {planLoading ? (
          <LoadingCard />
        ) : (
          <SubscriptionCard
            name={characteristics.name}
            icon={<AddonIcon kind={ProductInstanceTypes.Internet} className={styles['icon']} />}
          />
        )}
        {addonsLoading ? (
          <LoadingCard />
        ) : (
          currentAddOns?.map(({ name, kind }) => {
            return (
              <SubscriptionCard key={name} name={name} icon={<AddonIcon kind={kind} className={styles['icon']} />} />
            );
          })
        )}
        {!hasPreinstall && (
          <>
            <hr className={styles['separator']} />
            <Txt variant="heading6">{t('cart.disconnectDate.heading')}</Txt>
            {billingAccount.loading ? (
              <MultilineLoading lines={2} />
            ) : (
              <Txt variant="bodySmallRegular" pb={'16px'}>
                {nextBillingCycleDate
                  ? t('cart.disconnectDate.description', {
                      nextBillingCycleDate,
                    })
                  : t('cart.disconnectDate.descriptionError')}
              </Txt>
            )}
          </>
        )}
      </div>
      {!hasPreinstall && etf && (
        <>
          <hr className={styles['separator']} />
          <EarlyTerminationFee />
        </>
      )}
    </div>
  );
};

export default DisconnectCart;
